// MUI Theme
import { Tooltip } from '@mui/material';
import { ViewTimelineTwoTone, Visibility } from '@mui/icons-material';
import { Column } from '@tanstack/react-table';

// Services
import AuthService from '../../services/auth.service';

// Types
import { ResourceType, resourceTypeToPath } from '../../types/ResourceType';
import { TasksQueue, AssignedTaskQueue } from '../../types/TasksQueue';
import { SettingsState } from '../../redux/slices/settingsSlice';
import { AppPermissions } from '../../constants/Permissions';
import { TaskSeverity } from '../../types/TaskSeverity';

// Components
import TasksQueueOperator from '../../components/pages/tasksQueue/components/tasksQueueOperator';
import TasksQueueStatus from '../../components/pages/tasksQueue/components/tasksQueueStatus';

import { dateHeader, formatCellDate } from './timeModeCell';
import { linkTo } from '../linkTo';

export const makeTasksQueueColumn = (
  onOpenChangeLog: (id: string) => void,
  handleOpenTasksDetail: (data: any) => void,
  handleStatusChanged: (status: any) => void,
  settingStates: SettingsState
) => {
  const col: Column<any>[] = [
    {
      id: 'id',
      header: '',
      accessorKey: 'id',
      sortable: false,
      size: 20,
      cell: ({ row }) => {
        const { id } = row.original as TasksQueue;
        const handleClick = () => {
          onOpenChangeLog(id);
        };
        return (
          <Tooltip style={{ cursor: 'pointer', textAlign: 'center' }} title='Show Changelog' disableInteractive onClick={handleClick}>
            <ViewTimelineTwoTone fontSize='small' />
          </Tooltip>
        );
      },
    },
    {
      header: 'Watcher',
      sortable: false,
      enableColumnFilter: false,
      size: 60,
      cell: ({ row }) => {
        const { watchingUsers } = row.original as TasksQueue;
        return (
          <div
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => handleOpenTasksDetail(row.original)}
          >
            <Visibility fontSize='small' />
            <span style={{ paddingLeft: '6px' }}>{watchingUsers?.length ?? 0}</span>
          </div>
        );
      },
    },
    {
      header: 'Type',
      sortable: false,
      enableColumnFilter: false,
      size: 80,
      cell: ({ row }) => {
        const rowData: AssignedTaskQueue = row.original;
        const primaryResource = rowData.associatedResources.find((resource) => resource.isPrimary);
        return primaryResource ? ResourceType[primaryResource.resourceType] : ' ';
      },
    },
    {
      header: 'Category',
      accessorKey: 'category',
      enableColumnFilter: true,
      size: 200,
    },
    {
      header: 'Reference',
      accessorKey: 'displayName',
      headerClassName: 'react-table-header-align-center',
      enableColumnFilter: true,
      size: 650,
      cell: ({ row }) => {
        const rowData: AssignedTaskQueue = row.original;
        const primaryResource = rowData.associatedResources?.find((resource) => resource.isPrimary);
        if (primaryResource) {
          let path = resourceTypeToPath[primaryResource.resourceType];
          if (path === '/reader') {
            path = '/readers';
          }
          return linkTo(path, `${primaryResource.subjectId}`, row.original.displayName, '_blank');
        } else {
          return <span>{row.original.displayName}</span>;
        }
      },
    },
    {
      header: 'Title',
      accessorKey: 'title',
      enableColumnFilter: true,
      size: 460,
      cell: ({ row }) => <span>{row.original.title}</span>,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableColumnFilter: false,
      size: 120,
      cell: ({ row }) => {
        return AuthService.hasPermission(AppPermissions.TasksQueue.EditStatus) ? (
          <TasksQueueStatus
            taskStatus={row.original.status}
            taskId={row.original.id}
            handleStatusChanged={handleStatusChanged}
            settingStates={settingStates}
          />
        ) : (
          <span>
            {settingStates.taskStatus?.value.map((task) => {
              if (task.text === row.original.status) {
                const taskColor = settingStates.taskStatusColor?.value.find((v) => v.text === task.text);
                return (
                  <span key={task.text} style={{ color: taskColor?.value || '' }}>
                    {task.value}
                  </span>
                );
              }
              return null;
            })}
          </span>
        );
      },
    },
    {
      header: 'Assign To',
      accessorKey: 'assignedUserName',
      enableColumnFilter: true,
      size: 240,
      style: { height: 32, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' },
      cell: ({ row }) => {
        return AuthService.hasPermission(AppPermissions.TasksQueue.EditAssignee) ? (
          <TasksQueueOperator taskId={row.original.id} assignedUserName={row.original.assignedUserName} handleStatusChanged={handleStatusChanged} />
        ) : (
          <span>{row.original.assignedUserName ?? 'Not Assigned'}</span>
        );
      },
    },
    {
      header: 'Age',
      accessorKey: 'age',
      size: 90,
      cell: ({ row }) => row.original.age,
    },
    {
      header: 'Severity',
      accessorKey: 'severity',
      size: 90,
      cell: ({ row }) => {
        return <span>{TaskSeverity[row.original.severity]}</span>;
      },
    },
    {
      header: dateHeader('Created'),
      accessorKey: 'createdDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.createdDateTime),
    },
    {
      header: dateHeader('Modified'),
      accessorKey: 'modifiedDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.modifiedDateTime),
    },
  ] as Array<any>;

  return col;
};
